import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@antimatterhq/ui/lib/utils';

const radioVariants = cva(
  [
    'aspect-square h-4 w-4 rounded-full',
    'bg-white dark:bg-transparent border border-grey-300 dark:border-grey-650',
    'focus-visible:ring-blue-600 dark:focus-visible:ring-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'hover:bg-blue-600/8 dark:hover:bg-blue-400/8',
  ],
  {
    variants: {
      variant: {
        default: [
          'data-[state=checked]:bg-blue-600 dark:data-[state=checked]:bg-blue-500',
          'data-[state=checked]:border-blue-600 dark:data-[state=checked]:border-blue-500',
          'data-[state=checked]:text-white dark:data-[state=checked]:text-grey-800',
          'hover:border-blue-600 dark:hover:border-blue-500',
        ],
        success: [
          'data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500',
          'data-[state=checked]:text-white',
          'hover:border-green-600 dark:hover:border-green-500',
        ],
        warning: [
          'data-[state=checked]:bg-yellow-600 dark:data-[state=checked]:bg-yellow-500',
          'data-[state=checked]:text-white',
          'hover:border-yellow-600 dark:hover:border-yellow-500',
        ],
        error: [
          'data-[state=checked]:bg-red-600 dark:data-[state=checked]:bg-red-500',
          'data-[state=checked]:text-white',
          'hover:border-red-600 dark:hover:border-red-500',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type RadioGroupProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
> &
  VariantProps<typeof radioVariants> & {
    className?: string;
    label?: string;
  };

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupProps
>(({ className, variant = 'default', label, ...props }, ref) => {
  return (
    <div className="flex items-center space-x-2">
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(radioVariants({ variant }), className)}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="h-1 w-1 fill-current text-current" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {label && (
        <label className="text-sm text-grey-800 dark:text-grey-100">
          {label}
        </label>
      )}
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
    className?: string;
  }
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

export { RadioGroup, RadioGroupItem };
